import { inject } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from "@angular/router";
import { catchError, map, of } from "rxjs";

import { CuratorControllerService } from "../../../modules/lipidcompass-backend-client";
import { environment } from "../../environments/environment";

/**
 * Curator consent gate (design §5.2, #340 Task 10). Wired onto `/curate` and
 * `/admin/dataImport/submissions` ALONGSIDE `HasRoleGuard` (which handles the
 * login/role floor) -- this guard only handles the one-time consent capture:
 * a curator/admin principal must have an existing `CuratorConsent` record
 * (`GET /curators/consent/me` -> 200/204) before reaching a curation route.
 *
 * 200 -> admitted. A 204 (no consent recorded yet) or ANY request error (e.g.
 * a transient backend failure) is treated as "no consent" and redirects to
 * the consent screen, preserving the originally requested URL as `returnUrl`
 * so `CurateConsentComponent` can resume navigation once consent is recorded.
 */
export const CuratorConsentGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  // A disabled `curate` feature must not gate anything. This guard is also wired
  // onto `/admin/dataImport/submissions`, whose own `featureEnabled` is
  // `environment.features.admin` -- independently `true` in prod even when
  // `curate` is `false`. Without this no-op, every curator/admin would be
  // redirected to `/curate/consent`, which is itself gated on `features.curate`
  // and bounces to `/home`, locking them out of the pre-existing admin curation
  // page. This is distinct from the fail-closed error case below, which must
  // still redirect when the feature IS enabled.
  if (!environment.features.curate) {
    return true;
  }

  const curatorController = inject(CuratorControllerService);
  const router = inject(Router);

  const redirectToConsent = () =>
    router.createUrlTree(["/curate/consent"], {
      queryParams: { returnUrl: state.url },
    });

  return curatorController.curatorHasConsent("response").pipe(
    map((response) => (response.status === 200 ? true : redirectToConsent())),
    catchError(() => of(redirectToConsent()))
  );
};
